import { EventEmitter, Injectable, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';

import { OrganisationListItem } from '@assetaccountant/api/models/organisation-list-item';
import { OrganisationDto } from '@assetaccountant/api/models/organisation.dto';
import { NavItem } from '../models/nav-item';
import { NavigationContext } from '../models/navigation-context';
import { RegimeApiModel } from '../models/regime-api-model';
import { Register } from '../models/register';
import { createPeriodRange } from '../resolvers/period.resolver';
import { getChildRoute, navigateToRouteWithReplacedParameters } from '../utils/route-utils';
import { LayoutService } from './layout.service';
import { OrganisationService } from './organisation.service';
import { RegisterService } from './register.service';

@Injectable({
  providedIn: 'root',
})
export class AppNavigationService {
  @Output() onRegisterChanged = new EventEmitter<Register>();
  @Output() onOrganisationChanged = new EventEmitter<OrganisationListItem>();

  subNavigation = new Subject<NavItem[]>();

  private registers: Register[] = [];

  constructor(
    private router: Router,
    private registerService: RegisterService,
    private organisationService: OrganisationService,
    private layoutService: LayoutService,
  ) {}

  initializeRegisterData(registers: Register[]) {
    this.registers = registers;
  }

  getAllRegisters() {
    return this.registers.slice();
  }

  getRegisterById(id: number) {
    return this.registers.find(r => r.id === id);
  }

  getActiveRegister(): Register {
    return getChildRoute(this.router.routerState.snapshot.root).data.register;
  }

  getActiveOrganisation(): OrganisationDto {
    return getChildRoute(this.router.routerState.snapshot.root).data.organisation
      ?.activeOrganisation;
  }

  getActiveRegisterId(): number | null {
    // params are available when Guards/Resolvers are running, data isn't
    return +getChildRoute(this.router.routerState.snapshot.root).params.registerId || null;
  }

  getActiveOrganisationId(throwOnNull?: false): number | null;
  getActiveOrganisationId(throwOnNull: true): number;
  getActiveOrganisationId(throwOnNull?: boolean): number | null {
    const result =
      +getChildRoute(this.router.routerState.snapshot.root).params.organisationId || null;
    if (!result && throwOnNull) {
      throw new Error('organisationId is null');
    }
    return result;
  }

  setSubNavigation(sub: any) {
    this.subNavigation.next(sub);
  }

  switchRegister(nextActiveRegister: Register, ignoreSet = false) {
    this.registerService.recordAccess(nextActiveRegister.id).subscribe(_ => {});
    if (!ignoreSet) {
      this.setActiveRegister(nextActiveRegister);
    }
  }

  switchOrganisation(org: OrganisationListItem) {
    this.organisationService.recordAccess(org.organisationId).subscribe(_ => {});
    this.setActiveOrganisation(org);
    this.layoutService.setPaymentRequired(false);
  }

  async setActiveRegister(register: Register) {
    const activeRegister = this.getActiveRegister();
    if (!activeRegister || activeRegister.id !== register.id) {
      const navigated = await this.router.navigate([
        '/organisations',
        register.organisationId,
        'registers',
        register.id,
      ]);
      if (navigated) {
        this.onRegisterChanged.emit(register);
      }
    }
  }

  async setActiveOrganisation(org: OrganisationListItem) {
    const activeOrg = this.getActiveOrganisation();
    if (!activeOrg || activeOrg.organisationId !== org.organisationId) {
      const navigated = await this.router.navigate(['/organisations', org.organisationId]);
      if (navigated) {
        this.onOrganisationChanged.emit(org);
      }
    }
  }

  async setPeriod(periodStart: string, periodEnd: string) {
    await navigateToRouteWithReplacedParameters(this.router, {
      periodRange: createPeriodRange(periodStart, periodEnd),
    });
  }

  async setFinancialYear(fyRange: string) {
    await navigateToRouteWithReplacedParameters(this.router, {
      fyRange,
    });
  }

  getPeriodStartDate(): string | null {
    const period = getChildRoute(this.router.routerState.snapshot.root).data.period;
    return period ? period.startDate : null;
  }
  getPeriodEndDate(): string | null {
    const period = getChildRoute(this.router.routerState.snapshot.root).data.period;
    return period ? period.endDate : null;
  }

  async setRegime(regimeId: number) {
    await navigateToRouteWithReplacedParameters(this.router, {
      regimeId,
    });
  }

  getActiveRegime(): RegimeApiModel | null {
    return getChildRoute(this.router.routerState.snapshot.root).data.regime || null;
  }

  getActiveRegimeId(): number | null {
    // params are available when Guards/Resolvers are running, data isn't
    return +getChildRoute(this.router.routerState.snapshot.root).params.regimeId || null;
  }

  getContext(): NavigationContext {
    const context = new NavigationContext();
    context.registerId = this.getActiveRegisterId();
    context.periodEndDate = this.getPeriodEndDate();
    context.periodStartDate = this.getPeriodStartDate();
    context.regimeId = this.getActiveRegimeId();

    return context;
  }

  getCurrentNavigationItem(navigation: NavItem[]): NavItem | null {
    // Copy so we don't sort original array
    const _navigation = [...navigation];

    // Remove parameters from middle and querystrings from the end
    const urlSegments = this.router.url.replace(/;[^\/]+|\?.+$/g, '').split(/\/|#/);

    // Count number of matching route segments for each nav item
    _navigation.forEach(navItem => {
      let matchingRouteSegments = 0;
      urlSegments.forEach(urlSegment => {
        if (navItem.route.some(r => r === urlSegment)) {
          matchingRouteSegments++;
        }
      });
      navItem.matchingRouteSegmentsCount = matchingRouteSegments;
    });

    // Secondary sort by shortest route
    _navigation.sort((a, b) => a.route.length - b.route.length);
    // Primary sort by most route segment matches
    _navigation.sort(
      (a, b) => (b.matchingRouteSegmentsCount ?? 0) - (a.matchingRouteSegmentsCount ?? 0),
    );
    // Return best match
    return _navigation[0]?.matchingRouteSegmentsCount ? _navigation[0] : null;
  }
}
